// @flow
import React, { useContext, type Node } from 'react';

import { ID_LAYER_HEADER__SHOPPING__CRITERIA_MODAL } from 'Components/utils/identifiers/constants';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { t } from 'Src/i18n';

import { Button } from 'Components/frameworks/button/Button';
import { Layer } from 'Components/frameworks/layer/Layer';
import { Modal } from 'Components/frameworks/modal/Modal.jsx';

export type CriteriaModalProps = {
  children: Node,
  onClose?: () => mixed,
  onReset?: () => mixed,
  onValidate?: () => mixed,
};

export const CriteriaModal = ({ children, onClose, onReset, onValidate }: CriteriaModalProps) => {
  const { isDesktop } = useContext(ResponsiveContext);

  const [criteriaContext] = useContext(CriteriaContext);

  const buttons = (
    <div
      className={`relative p-3 px-6 lg:px-0 w-full flex items-center justify-between mt-auto bg-white${
        isDesktop ? '' : ' shadow'
      }`}
    >
      <Button
        theme="link"
        onClick={() => {
          onReset && onReset();
        }}
        className="text-sm cursor-pointer"
      >
        {t('faceting.reset')}
      </Button>
      <Button
        color={COLORS.WHITE}
        colorBg={COLORS.TURQUOISE}
        onClick={() => {
          onValidate && onValidate();
        }}
        className="text-sm cursor-pointer"
      >
        {t('faceting.validate')}
      </Button>
    </div>
  );

  const ariaLabel = t('faceting.when.duration');
  const closeLabel = t('common.close');

  const onCloseModal = () => {
    onClose && onClose();
  };

  return isDesktop ? (
    <Modal
      ariaLabel={ariaLabel}
      className="text-center px-10 py-5"
      closeLabel={closeLabel}
      onClose={() => {
        onClose && onClose();
      }}
      size="small"
    >
      {children}
      {buttons}
    </Modal>
  ) : (
    <Layer
      ariaLabel={ariaLabel}
      ariaLabelledby={ID_LAYER_HEADER__SHOPPING__CRITERIA_MODAL}
      buttonLeft={{
        iconName: ICONS.LEFT_ARROW,
        label: t('common.backNavigation'),
        onClick: onCloseModal,
      }}
      closeLabel={closeLabel}
      hasAnimateEnter
      header={
        <div
          className="text-center font-normal text-xl text-gray-darker"
          dangerouslySetInnerHTML={{
            __html: criteriaContext.layerHeader,
          }}
          id={ID_LAYER_HEADER__SHOPPING__CRITERIA_MODAL}
        />
      }
      onClose={onCloseModal}
    >
      <div className="scroll-y" style={{ height: 'calc(100% - 70px)' }}>
        {children}
      </div>
      {buttons}
    </Layer>
  );
};
